import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { Button, Input, Label, Modal } from '@atrvd/saas-ui'
import { useRenameBankConnection } from '@/hooks/useBankConnections'
import {
  connectionRenameSchema,
  type ConnectionRenameFormData,
} from '@/lib/connection-rename-form'
import type { BankConnectionRow } from '@/lib/db-tenant'
import { CONNECTION_COPY } from './connectionManagement.utils'

/** Props for {@link RenameConnectionDialog}. */
interface RenameConnectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The connection being renamed; null while no row is selected. */
  connection: BankConnectionRow | null
}

/**
 * Rename a bank connection (DCP-324, AC#5).
 *
 * Mirrors `EditPackDialog`: one required field on react-hook-form + Zod inside a
 * `Modal`, with a `prevIdRef` guard so a background refetch that mints a new
 * `connection` object for the SAME row does not wipe the user's in-progress
 * edit. The connection list is polled/invalidated by several sibling actions, so
 * that guard is not theoretical here.
 *
 * Seeds from `display_name`, not from `name`: a connection that has never been
 * renamed has `name === null`, and opening the dialog on an empty field would
 * make "rename" look like "name for the first time" while hiding the label the
 * user actually sees in the table.
 */
export function RenameConnectionDialog({
  open,
  onOpenChange,
  connection,
}: RenameConnectionDialogProps) {
  const rename = useRenameBankConnection()

  /**
   * Last server-side error, shown inline above the field so it stays readable
   * after the transient toast fades.
   */
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConnectionRenameFormData>({
    resolver: zodResolver(connectionRenameSchema),
  })

  /** Row the form was last seeded from; cleared on close so reopening re-seeds. */
  const prevIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!open) {
      prevIdRef.current = null
      return
    }
    if (!connection) return
    if (connection.id === prevIdRef.current) return
    reset({ name: connection.display_name ?? '' })
    prevIdRef.current = connection.id
    setServerError(null)
  }, [open, connection, reset])

  /** Close and drop all transient state. */
  const handleClose = () => {
    reset()
    setServerError(null)
    onOpenChange(false)
  }

  /**
   * Submit the rename. The hook owns the success toast and the cache
   * invalidation; this handler only decides whether to close.
   *
   * @param values - Validated form values
   */
  const onSubmit = async (values: ConnectionRenameFormData) => {
    if (!connection) return
    setServerError(null)
    try {
      await rename.mutateAsync({ id: connection.id, name: values.name })
      handleClose()
    } catch (error) {
      // Kept open with the message inline: the name is still in the field, so
      // the user can correct and retry instead of retyping it.
      setServerError(error instanceof Error ? error.message : 'Failed to rename connection')
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title={CONNECTION_COPY.renameTitle}
      description={CONNECTION_COPY.renameDescription}
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={handleClose} disabled={rename.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="rename-connection-form" disabled={rename.isPending}>
            {rename.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </div>
      }
    >
      <form
        id="rename-connection-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 px-1"
      >
        {serverError && (
          <div
            role="alert"
            className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
          >
            {serverError}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="rename-connection-name">
            {CONNECTION_COPY.renameLabel} <span className="text-destructive">*</span>
          </Label>
          <Input
            id="rename-connection-name"
            placeholder={CONNECTION_COPY.renamePlaceholder}
            disabled={rename.isPending}
            className="aria-[invalid=true]:border-destructive"
            aria-invalid={!!errors.name}
            {...register('name')}
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>
      </form>
    </Modal>
  )
}
